import { defaultError } from '@qpr/client-library';
import api, { ApiRequest, ApiResult } from '../../../api/api-caller';
import initialState, { ConfigurableGridState, Data } from './configurable-grid-state';

type MainProcessData = Record<string, string>;

const fetchDataFromApi = async (id: string): Promise<ApiResult> => {
  const req: ApiRequest = {
    method: 'GET'
  };

  const result: ApiResult = await api.asyncCallApi(
    `viewdefinitions${id === '' ? '?isDefault=true' : `/${id}`}`,
    req
  );

  return result;
};

const putData = async (id: string, dataToUpdate: any): Promise<ApiResult> => {
  const req: ApiRequest = {
    method: 'PUT',
    body: dataToUpdate
  };
  const result: ApiResult = await api.asyncCallApi(`viewdefinitions/${id}`, req);

  return result;
};

const fetchMainProcessFromApi = async (id: string, attributes: string[]): Promise<ApiResult> => {
  const query: ApiRequest = {
    body: {
      query: `[${id}]`,
      attributes: attributes.join(',')
    },
    method: 'POST'
  };
  const result: ApiResult = await api.asyncCallApi('meaquery', query);

  return result;
};

const extractData = (result: ApiResult): Data | Data[] | MainProcessData[] => {
  const { data } = result;
  return data;
};

const errorHandler = (apiResult: ApiResult): ConfigurableGridState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

// initialize arrays
const attributesToReplace: string[] = [];
const stringsToReplace: string[] = [];

const findStringsToReplace = (value: string) => {
  const regexp = RegExp('@mainprocess.([a-zåäöÅÄÖ.]*)', 'g');

  let match = regexp.exec(value);

  while (match !== null) {
    attributesToReplace.push(match[1]);
    stringsToReplace.push(match[0]);
    match = regexp.exec(value);
  }
};

const findAllToReplace = (dataToSearch: any) => {
  Object.entries(dataToSearch).forEach((entry: any) => {
    if (typeof entry[1] === 'string') findStringsToReplace(entry[1]);

    if (typeof entry[1] === 'object' && entry[1] !== null) findAllToReplace(entry[1]);
  });
};

// eslint-disable-next-line no-shadow
const replaceStrings = (value: string, MainProcessData: MainProcessData[]) => {
  let updatedValue = value;
  const regexp = RegExp('@mainprocess.([a-zåäöÅÄÖ.]*)', 'g');
  let match = regexp.exec(updatedValue);

  while (match !== null) {
    if (MainProcessData[0][`${match[1]}`] !== undefined) {
      updatedValue = updatedValue.replace(
        match[0],
        MainProcessData[0] !== undefined ? MainProcessData[0][`${match[1]}`] : ''
      );
    }
    match = regexp.exec(updatedValue);
  }

  return updatedValue;
};

const doGetData = async (id: string, viewDefinitionId: string): Promise<ConfigurableGridState> => {
  try {
    const result = await fetchDataFromApi(viewDefinitionId);

    if (!result.ok) {
      return errorHandler(result);
    }

    let data = extractData(result) as Data;

    if (!data) return initialState;

    // check all values of string values of viewdefinition data and find what needs to be replaced ("@mainprocess.xxxxx")
    findAllToReplace(data);

    const uniqueAttributesToReplace = attributesToReplace.filter((value, index) => {
      return attributesToReplace.indexOf(value) === index;
    });

    // eslint-disable-next-line no-shadow
    let MainProcessData: MainProcessData[] = [{ id }];
    if (
      id !== '' &&
      uniqueAttributesToReplace.length > 0 &&
      !(uniqueAttributesToReplace.length === 1 && uniqueAttributesToReplace[0] === 'id')
    ) {
      // get needed attributes from MEA
      const mainProcessResult = await fetchMainProcessFromApi(id, uniqueAttributesToReplace);

      if (!mainProcessResult.ok) {
        return errorHandler(mainProcessResult);
      }
      MainProcessData = extractData(mainProcessResult) as MainProcessData[];
    }

    // replace all @mainprocess.xxxx with data from MEA
    const replaceEach = (dataToReplace: any) => {
      const replacedData = dataToReplace;
      Object.entries(dataToReplace).forEach((entry: any) => {
        if (typeof entry[1] === 'string')
          replacedData[`${entry[0]}`] = replaceStrings(entry[1], MainProcessData);

        if (typeof entry[1] === 'object' && entry[1] !== null) replaceEach(entry[1]);
      });

      return replacedData;
    };

    data = replaceEach(data);
    return {
      ...initialState,
      data
    };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface ViewModelType {
  fetchData(): Promise<ConfigurableGridState>;
  getData(): Promise<ConfigurableGridState>;
  getId(): string;
  updateData(id: string, dataToUpdate: any): void;
}

interface ViewModelProps {
  id?: string;
  viewDefinitionId?: string;
}

const createViewModel = ({ id = '', viewDefinitionId = '' }: ViewModelProps): ViewModelType => {
  let data: ConfigurableGridState;
  const cacheKey: string = `${id}${viewDefinitionId}`;

  const getId = (): string => id;
  const fetchData = async (): Promise<ConfigurableGridState> => {
    if (viewModelCache[cacheKey]) {
      data = viewModelCache[cacheKey];
    } else {
      data = await doGetData(id, viewDefinitionId);
      viewModelCache[cacheKey] = data;
    }
    return data;
  };

  const getData = async (): Promise<ConfigurableGridState> => {
    if (data) {
      return data;
    }
    await fetchData();
    return data;
  };

  const updateData = async (idToUpdate: string, dataToUpdate: any): Promise<Boolean> => {
    await putData(idToUpdate, dataToUpdate);
    return true;
  };

  return {
    getId,
    fetchData,
    getData,
    updateData
  };
};

export default createViewModel;
